import React, { useState, useEffect } from 'react';
import { Text, Flex, Dialog, Button, TextField, TextArea } from "@radix-ui/themes";
import { mdiChevronDown, mdiChevronUp, mdiEye, mdiCreation, mdiDelete, mdiCached, mdiAccount, mdiFeather, mdiContentSave, mdiClose, mdiAutorenew } from '@mdi/js';
import { Icon } from '@mdi/react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useStore } from '../store/store';

const SceneSummary = ({ scene, chapter, novel }) => {
    const navigate = useNavigate();
    const [state, actions] = useStore();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    
    // New state for edit mode
    const [isEditing, setIsEditing] = useState(false);
    const [editedSummary, setEditedSummary] = useState(scene.summary || '');
    const [editedPov, setEditedPov] = useState(scene.pov || '');
    const [isSaving, setIsSaving] = useState(false);
    
    // New state for generating summary
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

    // If this scene has been deleted, don't render anything
    if (isDeleted) {
        return null;
    }

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // Check if this scene already has prose content
    const hasExistingProse = scene.content && scene.content.length > 0;

    // Handle navigation to scene prose page
    const handleViewProse = () => {
        navigate(`/novel/${novel.id}/chapter/${chapter.id}/scene/${scene.id}`);
    };
    
    // Handle edit scene button click
    const handleEditScene = () => {
        setEditedSummary(scene.summary || '');
        setEditedPov(scene.pov || '');
        setIsEditing(true);
        setIsExpanded(true); // Always expand when editing
    };
    
    // Save edited scene details
    const saveChanges = async () => {
        setIsSaving(true);
        try {
            // Update both summary and POV using the new API method
            await api.updateSceneDetails(scene.id, editedSummary, editedPov);
            
            // Create a deep copy of the novel to update
            const updatedNovel = JSON.parse(JSON.stringify(novel));
            const updatedChapter = updatedNovel.chapters.find(c => c.id === chapter.id);
            const updatedScene = updatedChapter.scenes.find(s => s.id === scene.id);
            
            // Update scene properties
            updatedScene.summary = editedSummary;
            updatedScene.pov = editedPov;
            
            // Update the novel in the store
            actions.setCurrentNovel(updatedNovel);
            
            // Exit edit mode
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving scene changes:', error);
            alert('Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };
    
    // Dismiss changes without saving
    const dismissChanges = () => {
        setIsEditing(false);
    };
    
    // Handle generate summary button click
    const handleGenerateSummary = async () => {
        if (isGeneratingSummary || !hasExistingProse) return;
        
        setIsGeneratingSummary(true);
        try {
            // Call API to generate summary from existing prose
            const result = await api.generateSceneSummary(scene.id);
            
            if (result.success && result.summary) {
                // Create a deep copy of the novel to update
                const updatedNovel = JSON.parse(JSON.stringify(novel));
                const updatedChapter = updatedNovel.chapters.find(c => c.id === chapter.id);
                const updatedScene = updatedChapter.scenes.find(s => s.id === scene.id);
                
                // Update scene property
                updatedScene.summary = result.summary;
                
                // Update the novel in the store
                actions.setCurrentNovel(updatedNovel);
                
                console.log(`Successfully generated summary for scene ${scene.scene_number}`);
            } else {
                console.error('Summary generation failed:', result.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Error generating summary:', error);
        } finally {
            setIsGeneratingSummary(false);
        }
    };
    
    // Handle generate prose button click
    const handleGenerateButtonClick = () => {
        if (isGenerating) return;
        
        // If there is existing prose, show confirmation dialog
        if (hasExistingProse) {
            setConfirmModalOpen(true);
        } else {
            // Otherwise directly generate prose
            generateProse();
        }
    };
    
    // Generate prose for the scene
    const generateProse = async () => {
        setIsGenerating(true);
        try {
            // Call API to generate prose
            const result = await api.generateSceneProse(novel.id, chapter.id, scene.id);
            
            if (result.success && result.prose) {
                // Update the scene with new prose and summary
                scene.content = result.prose;
                if (result.summary) {
                    scene.summary = result.summary;
                }
                
                console.log(`Successfully generated prose for scene ${scene.scene_number}`);
            } else {
                console.error('Prose generation failed:', result.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Error generating prose:', error);
        } finally {
            setIsGenerating(false);
            // Close confirmation dialog if open
            setConfirmModalOpen(false);
        }
    };
    
    // Handle regenerate confirmation
    const handleRegenerate = () => {
        setConfirmModalOpen(false);
        generateProse();
    };

    // Handle delete button click
    const handleDeleteButtonClick = () => {
        setDeleteModalOpen(true);
    };
    
    // Delete scene function
    const deleteScene = async () => {
        setIsDeleting(true);
        setDeleteModalOpen(false);
        
        try {
            const result = await api.deleteScene(scene.id);
            
            if (result.success) {
                // Mark as deleted to prevent rendering
                setIsDeleted(true);
                
                // Create a deep copy of the novel object
                const updatedNovel = JSON.parse(JSON.stringify(novel));
                
                // Find the chapter and remove the scene
                const chapterToUpdate = updatedNovel.chapters.find(c => c.id === chapter.id);
                if (chapterToUpdate) {
                    // Remove the scene from the chapter
                    chapterToUpdate.scenes = chapterToUpdate.scenes.filter(s => s.id !== scene.id);
                    
                    // Update scene numbers for remaining scenes
                    chapterToUpdate.scenes.forEach((s, index) => {
                        s.scene_number = index + 1;
                    });
                    
                    // Update the novel in the store
                    actions.setCurrentNovel(updatedNovel);
                    
                    console.log(`Successfully deleted scene ${scene.scene_number}`);
                }
            } else {
                console.error('Scene deletion failed:', result.error || 'Unknown error');
                // You could add toast notification for error here
            }
        } catch (error) {
            console.error('Error deleting scene:', error);
            // You could add toast notification for error here
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div>
            <div className='chapter-summary-header-wrapper'>
                <Flex>
                    <Text as="div" className='chapter-summary-header-chapter-title'>{state.currentNovel.type === 'non-fiction' ? `${scene.scene_number}. ${scene.title}` : `Scene ${scene.scene_number}`}</Text>
                    <div className='chapter-summary-header-info'>
                        {isEditing ? (
                            <TextField.Root 
                                className="sidebar-form-textInput" 
                                style={{ width: '150px', marginBottom: '5px', marginLeft: '70px' }}
                                placeholder="POV Character" 
                                value={editedPov} 
                                variant="soft" 
                                onChange={(e) => setEditedPov(e.target.value)}
                            />
                        ) : (
                            <div className='chapter-summary-header-info-icon'>
                                <Icon path={mdiAccount} size={1} />{scene.pov}
                            </div>
                        )}
                        <div className='chapter-summary-header-info-icon'>
                            <Icon path={mdiFeather} size={1} />{scene.words} words ({scene.reading_time_mins} min)
                        </div>
                    </div>
                </Flex>
            </div>
            <Flex className='chapter-summary-content-wrapper'>
                <div style={{width: '65%'}}>
                    {isEditing ? (
                        <TextArea
                            className="sidebar-form-textInput"
                            value={editedSummary}
                            placeholder="Scene summary..."
                            variant="soft"
                            onChange={(e) => setEditedSummary(e.target.value)}
                            style={{
                                width: '100%',
                                minHeight: '200px',
                                padding: '10px',
                                fontFamily: 'Libre Baskerville',
                                backgroundColor: 'rgb(0 0 0 / 9%)'
                            }}
                        />
                    ) : (
                        <>
                            <div className={`chapter-summary-content ${isExpanded ? 'chapter-summary-content-extended' : ''}`}>
                                {(!scene.summary || scene.summary === "") ? 'No content yet' : scene.summary}
                            </div>
                            <div className='chapter-summary-content-expand' onClick={toggleExpand}>
                                <Icon path={isExpanded ? mdiChevronUp : mdiChevronDown} size={1} />
                            </div>
                        </>
                    )}
                </div>
                
                {isEditing ? (
                    <div className='chapter-summary-tools' style={{ opacity: 1 }}>
                        <Flex className='chapter-summary-tools-item' onClick={saveChanges} style={{ opacity: isSaving ? 0.6 : 1 }}>
                            <div className='chapter-summary-tools-icon'>
                                <Icon 
                                    path={mdiContentSave} 
                                    size={1} 
                                    style={{
                                        marginTop: '6px', 
                                        marginLeft: '-13px', 
                                        transform: 'scale(0.8)',
                                        animation: isSaving ? 'spin 1s linear infinite' : 'none'
                                    }}
                                />
                            </div>
                            <div className='chapter-summary-tools-text'>
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </div>
                        </Flex>
                        <Flex className='chapter-summary-tools-item' onClick={dismissChanges}>
                            <div className='chapter-summary-tools-icon'>
                                <Icon path={mdiClose} size={1} style={{marginTop: '6px', marginLeft: '-13px', transform: 'scale(0.8)'}}/>
                            </div>
                            <div className='chapter-summary-tools-text'>
                                Dismiss Changes
                            </div>
                        </Flex>
                    </div>
                ) : (
                    <div className='chapter-summary-tools'>
                        <Flex className='chapter-summary-tools-item' onClick={handleViewProse}>
                            <div className='chapter-summary-tools-icon'>
                                <Icon path={mdiEye} size={1} style={{marginTop: '6px', marginLeft: '-13px', transform: 'scale(0.8)'}}/>
                            </div>
                            <div className='chapter-summary-tools-text'>
                                View Prose
                            </div>
                        </Flex>
                        
                        {!state.readOnly && (
                            <>
                                <Flex className='chapter-summary-tools-item' onClick={handleEditScene}>
                                    <div className='chapter-summary-tools-icon'>
                                        <Icon path={mdiFeather} size={1} style={{marginTop: '6px', marginLeft: '-13px', transform: 'scale(0.8)'}}/>
                                    </div>
                                    <div className='chapter-summary-tools-text'>
                                        Edit Scene Summary
                                    </div>
                                </Flex>
                                {hasExistingProse && (
                                    <Flex className='chapter-summary-tools-item' onClick={handleGenerateSummary}>
                                        <div className='chapter-summary-tools-icon'>
                                            <Icon 
                                                path={mdiAutorenew} 
                                                size={1} 
                                                className={isGeneratingSummary ? 'icon-spin' : ''}
                                                style={{
                                                    marginTop: '6px', 
                                                    marginLeft: '-14px', 
                                                    transform: 'scale(0.8)'
                                                }}
                                            />
                                        </div>
                                        <div className='chapter-summary-tools-text'>
                                            {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
                                        </div>
                                    </Flex>
                                )}
                                <Flex className='chapter-summary-tools-item' onClick={handleGenerateButtonClick}>
                                    <div className='chapter-summary-tools-icon'>
                                        <Icon 
                                            path={isGenerating ? mdiCached : mdiCreation} 
                                            size={1} 
                                            className={isGenerating ? 'icon-spin' : ''}
                                            style={{
                                                marginTop: '6px', 
                                                marginLeft: '-14px', 
                                                transform: 'scale(0.8)'
                                            }}
                                        />
                                    </div>
                                    <div className='chapter-summary-tools-text'>
                                        {isGenerating ? 'Generating...' : (hasExistingProse ? 'Re-generate Prose' : 'Generate Prose')}
                                    </div>
                                </Flex>
                                <Flex className='chapter-summary-tools-item' onClick={handleDeleteButtonClick}>
                                    <div className='chapter-summary-tools-icon'>
                                        <Icon 
                                            path={mdiDelete} 
                                            size={1} 
                                            className={isDeleting ? 'icon-spin' : ''}
                                            style={{
                                                marginTop: '6px', 
                                                marginLeft: '-13px', 
                                                transform: 'scale(0.8)'
                                            }}
                                        />
                                    </div>
                                    <div className='chapter-summary-tools-text'>
                                        {isDeleting ? 'Deleting...' : 'Delete Scene'}
                                    </div>
                                </Flex>
                            </>
                        )}
                    </div>
                )}
            </Flex>
            
            {/* Dialog modals remain unchanged */}
            <Dialog.Root open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
                <Dialog.Content>
                    <Dialog.Title>Re-generate Prose?</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        This scene already has prose content. Re-generating prose will replace all existing content. This action cannot be undone.
                    </Dialog.Description>
                    
                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </Dialog.Close>
                        <Button onClick={handleRegenerate} variant="solid" color="red">
                            Re-generate Prose
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
            <Dialog.Root open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <Dialog.Content>
                    <Dialog.Title>Delete Scene?</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        Are you sure you want to delete Scene {scene.scene_number}?
                        {scene.content && scene.content.length > 0 && (
                            <> This will delete all prose content for this scene.</>
                        )}
                        <br /><br />
                        This action cannot be undone.
                    </Dialog.Description>
                    
                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </Dialog.Close>
                        <Button onClick={deleteScene} variant="solid" color="red">
                            Delete Scene
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    );
};

export default SceneSummary;